import React from 'react';
import { FiUploadCloud, FiCheck, FiAlertCircle, FiX, FiLoader } from 'react-icons/fi';
import { useUpload } from '../contexts/UploadContext';

/**
 * Minimized upload indicator - shows when uploads are in progress
 * but the full panel is closed/minimized. Click to reopen panel.
 */
export default function UploadIndicator() {
  const context = useUpload();

  // Not available (outside provider) or nothing to show
  if (!context) {
    return null;
  }

  const {
    uploading,
    panelMinimized,
    totalFiles,
    uploadedCount,
    completedCount,
    processingCount,
    failedCount,
    attentionCount,
    isComplete,
    openPanel,
    dismissIndicator,
  } = context;

  const reopen = () => {
    window.dispatchEvent(new Event('photos:reopen-uploader'));
    openPanel();
  };

  // Only show when minimized and there are uploads
  if (!panelMinimized || totalFiles === 0) {
    return null;
  }

  // "Complete" means compute is also done, not just bytes uploaded.
  const allAccountedFor = (completedCount + processingCount + failedCount) >= totalFiles;
  const computeDone = processingCount === 0;
  const actuallyComplete = isComplete || (!uploading && allAccountedFor && computeDone && totalFiles > 0);

  const inProgress = !actuallyComplete && (uploading || processingCount > 0);
  const visibleAttentionCount = attentionCount ?? failedCount;
  const hasErrors = visibleAttentionCount > 0;

  // Calculate progress percentage based on files uploaded to S3
  const progressPercent = totalFiles > 0 ? Math.round((uploadedCount / totalFiles) * 100) : 0;

  // Files still being uploaded (not yet reached S3)
  const activeUploads = Math.max(0, totalFiles - uploadedCount - failedCount);

  return (
    <div className={`upload-indicator ${actuallyComplete ? 'complete' : ''} ${hasErrors ? 'has-errors' : ''}`}>
      {/* Progress bar at top of panel */}
      {inProgress && (
        <div className="upload-indicator-bar">
          <div
            className="upload-indicator-bar-fill"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      )}

      <div className="upload-indicator-content" role="button" tabIndex={0} aria-label="Reopen uploader" onClick={reopen} onKeyDown={e => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); reopen(); } }}>
        <div className="upload-indicator-icon">
          {actuallyComplete ? (
            hasErrors ? <FiAlertCircle /> : <FiCheck />
          ) : (
            processingCount > 0 && !uploading
              ? <FiLoader className="uploading" />
              : <FiUploadCloud className={inProgress ? 'uploading' : ''} />
          )}
        </div>

        <div className="upload-indicator-text">
          {actuallyComplete ? (
            hasErrors ? (
              <>
                <span className="upload-indicator-status">{completedCount} ready</span>
                <span className="upload-indicator-error">{visibleAttentionCount} need attention</span>
              </>
            ) : (
              <span className="upload-indicator-status">{completedCount} photos ready</span>
            )
          ) : (
            <>
              <span className="upload-indicator-status">
                {uploading
                  ? (activeUploads > 0 ? 'Uploading in background' : 'Finishing uploads')
                  : (processingCount > 0 ? 'Processing in background' : 'Finishing up')}
              </span>
              <span className="upload-indicator-detail">
                {uploading
                  ? 'You can keep browsing while your photos upload.'
                  : 'Your library will update automatically.'}
                {hasErrors && ` · ${visibleAttentionCount} need attention`}
              </span>
            </>
          )}
        </div>

        {actuallyComplete && (
          <button
            className="upload-indicator-dismiss"
            onClick={(e) => {
              e.stopPropagation();
              dismissIndicator();
            }}
            title="Dismiss"
          >
            <FiX />
          </button>
        )}
      </div>
    </div>
  );
}
